import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    GraduationCap, BookOpen, CheckCircle2, Lock, Trophy, Award,
    ArrowRight, Loader2, Crown, Sparkles, RefreshCcw, ChevronDown,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import LanguagePicker from "@/components/LanguagePicker";

export default function AgentTraining() {
    const { user } = useAuth();
    const { lang } = useLang();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(null);
    const [openLesson, setOpenLesson] = useState(null);
    const [quizMode, setQuizMode] = useState(false);
    const [quizData, setQuizData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const reload = async () => {
        try {
            const [c, p] = await Promise.all([
                api.get(`/training/modules?lang=${lang}`),
                user ? api.get("/training/progress") : Promise.resolve({ data: null }),
            ]);
            setCatalog(c.data);
            setProgress(p.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, lang]);

    const completedSet = new Set(progress?.lessons_completed || []);
    const allDone = catalog && completedSet.size >= catalog.total_lessons;
    const passed = !!progress?.passed_at;
    const lastAttempt = (progress?.quiz_attempts || []).slice(-1)[0];

    const completeLesson = async (lid) => {
        if (!user) {
            toast.info("Sign in to track your progress.");
            navigate("/login?next=/agents/training");
            return;
        }
        setCompleting(lid);
        try {
            await api.post(`/training/lesson/${lid}/complete`);
            toast.success("Lesson marked complete.");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
        } finally {
            setCompleting(null);
        }
    };

    const startQuiz = async () => {
        try {
            const { data } = await api.get(`/training/quiz?lang=${lang}`);
            setQuizData(data);
            setAnswers({});
            setResult(null);
            setQuizMode(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load quiz");
        }
    };

    const submitQuiz = async () => {
        if (Object.keys(answers).length < (quizData?.questions || []).length) {
            toast.error("Please answer every question.");
            return;
        }
        setSubmitting(true);
        try {
            const ordered = (quizData.questions || []).map((q) => answers[q.id]);
            const { data } = await api.post("/training/quiz/submit", { answers: ordered });
            setResult(data);
            await reload();
            if (data.passed) {
                toast.success(`🎉 Certified! Score: ${data.score_pct}%`);
            } else {
                toast.warning(`Score ${data.score_pct}% — need ${data.pass_threshold_pct}%. Review and retake.`);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !catalog) {
        return (
            <div className="min-h-screen grid place-items-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-coral" />
            </div>
        );
    }

    return (
        <div data-testid="agent-training-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="bg-ocean-deep-grad text-white py-12 lg:py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-4">
                        <GraduationCap className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            Certified Bula Agent · Training & Quiz
                        </span>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
                        Become a <span className="italic text-turquoise">Certified Bula Agent</span>.
                    </h1>
                    <p className="font-app text-sm sm:text-base text-white/85 max-w-3xl">
                        8 quick lessons. 1 final quiz. ~2 hours total. Pass with {catalog.pass_threshold_pct}%+ to unlock the
                        Certified badge — appears on every listing you publish, gives you a search ranking boost, and unlocks Gold-tier perks.
                    </p>
                    {user && passed && (
                        <Badge data-testid="certified-badge" className="mt-4 bg-coral text-white text-xs px-3 py-1.5">
                            <Crown className="h-3.5 w-3.5 mr-1.5" /> Certified Bula Agent
                        </Badge>
                    )}
                    <div className="mt-5">
                        <LanguagePicker variant="dark" testId="training-lang-select" />
                    </div>
                </div>
            </section>

            {/* PROGRESS STRIP */}
            {user && (
                <section className="py-5 border-b border-border bg-card" data-testid="training-progress-strip">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4">
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="font-app text-xs uppercase tracking-wider text-muted-foreground">Lessons</span>
                                <span className="font-app text-xs font-bold text-foreground tabular-nums">
                                    {completedSet.size} / {catalog.total_lessons}
                                </span>
                            </div>
                            <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                                <div
                                    className="h-full bg-fiji-green transition-all"
                                    style={{ width: `${(completedSet.size / catalog.total_lessons) * 100}%` }}
                                />
                            </div>
                        </div>
                        {lastAttempt && (
                            <div className="text-right shrink-0">
                                <div className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">Last attempt</div>
                                <div className={`font-display text-lg font-bold tabular-nums ${lastAttempt.passed ? "text-fiji-green" : "text-coral"}`}>
                                    {lastAttempt.score_pct}%
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section className="py-10 lg:py-14">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {quizMode && quizData ? (
                        <QuizPanel
                            data={quizData}
                            answers={answers}
                            setAnswers={setAnswers}
                            submitting={submitting}
                            result={result}
                            onSubmit={submitQuiz}
                            onCancel={() => { setQuizMode(false); setResult(null); }}
                        />
                    ) : (
                        <>
                            <div className="grid sm:grid-cols-3 gap-4 mb-8">
                                <Stat icon={BookOpen} label="Lessons" value={catalog.total_lessons} />
                                <Stat icon={GraduationCap} label="Estimated time" value={`${catalog.total_minutes} min`} accent />
                                <Stat icon={Trophy} label="Pass threshold" value={`${catalog.pass_threshold_pct}%`} />
                            </div>

                            <h2 className="font-display text-xl lg:text-2xl font-bold text-foreground mb-4">Modules</h2>
                            <div className="space-y-3 mb-8">
                                {catalog.modules.map((m) => {
                                    const done = completedSet.has(m.id);
                                    const open = openLesson === m.id;
                                    return (
                                        <Card
                                            key={m.id}
                                            data-testid={`lesson-${m.id}`}
                                            className={`p-4 lg:p-5 bg-card transition border ${done ? "border-fiji-green/40" : "border-border"}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setOpenLesson(open ? null : m.id)}
                                                className="w-full flex items-start gap-3 text-left"
                                            >
                                                <div className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${done ? "bg-fiji-green/15" : "bg-coral/10"}`}>
                                                    {done
                                                        ? <CheckCircle2 className="h-4 w-4 text-fiji-green" />
                                                        : <span className="font-display text-sm font-bold text-coral tabular-nums">{m.order}</span>
                                                    }
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                                        <h3 className="font-display text-base font-bold text-foreground">{m.title}</h3>
                                                        <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
                                                            {m.duration_min} min
                                                        </Badge>
                                                        {done && <Badge className="bg-fiji-green text-white text-[10px]">Done</Badge>}
                                                    </div>
                                                    <p className="font-app text-xs text-muted-foreground leading-relaxed">{m.summary}</p>
                                                </div>
                                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition shrink-0 ${open ? "rotate-180" : ""}`} />
                                            </button>
                                            {open && (
                                                <div className="mt-3 pt-3 border-t border-border space-y-2">
                                                    <ul className="space-y-1.5 mb-3">
                                                        {m.key_points.map((kp) => (
                                                            <li key={kp} className="flex items-start gap-2 text-sm font-app text-foreground/85">
                                                                <Sparkles className="h-3 w-3 text-coral mt-1 shrink-0" />
                                                                <span>{kp}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => completeLesson(m.id)}
                                                        disabled={done || completing === m.id}
                                                        data-testid={`lesson-complete-${m.id}`}
                                                        className={`font-app ${done ? "bg-fiji-green hover:bg-fiji-green/90" : "bg-coral hover:bg-coral-deep"} text-white`}
                                                    >
                                                        {completing === m.id && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                                                        {done ? "Completed" : "Mark complete"}
                                                    </Button>
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })}
                            </div>

                            <Card className={`p-5 lg:p-6 ${allDone ? "bg-coral/5 dark:bg-coral/10 border-coral/40" : "bg-card border-border"}`} data-testid="quiz-launcher">
                                <div className="flex items-start gap-4">
                                    <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${allDone ? "bg-coral/15" : "bg-secondary/60"}`}>
                                        {allDone ? <Award className="h-5 w-5 text-coral" /> : <Lock className="h-5 w-5 text-muted-foreground" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-display text-base font-bold text-foreground mb-1">
                                            {passed ? "You're certified" : "Final certification quiz"}
                                        </div>
                                        <p className="font-app text-sm text-muted-foreground mb-3">
                                            {passed
                                                ? "Your Certified Bula Agent badge is now visible site-wide. You can retake the quiz any time to refresh your knowledge."
                                                : allDone
                                                    ? "Complete the 10-question quiz. Score ≥ 75% to unlock your Certified Bula Agent badge."
                                                    : `Finish all ${catalog.total_lessons} lessons before taking the quiz.`}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                onClick={startQuiz}
                                                disabled={!user || !allDone}
                                                data-testid="start-quiz-btn"
                                                className="bg-coral hover:bg-coral-deep text-white font-app"
                                            >
                                                {passed ? <RefreshCcw className="h-4 w-4 mr-2" /> : <Award className="h-4 w-4 mr-2" />}
                                                {passed ? "Retake quiz" : "Start quiz"}
                                            </Button>
                                            {!user && (
                                                <Button asChild variant="outline" className="font-app">
                                                    <Link to="/login?next=/agents/training">
                                                        Sign in to begin <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                                    </Link>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <Card className={`p-4 ${accent ? "bg-coral/5 dark:bg-coral/10 border-coral/30" : "bg-card"}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`h-3.5 w-3.5 ${accent ? "text-coral" : "text-muted-foreground"}`} />
                <span className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums ${accent ? "text-coral" : "text-foreground"}`}>
                {value}
            </div>
        </Card>
    );
}

function QuizPanel({ data, answers, setAnswers, submitting, result, onSubmit, onCancel }) {
    const setAnswer = (qid, idx) => setAnswers({ ...answers, [qid]: idx });

    if (result) {
        return (
            <Card data-testid="quiz-result" className={`p-6 lg:p-8 ${result.passed ? "bg-fiji-green/5 dark:bg-fiji-green/10 border-fiji-green/40" : "bg-coral/5 dark:bg-coral/10 border-coral/40"}`}>
                <div className="text-center mb-5">
                    {result.passed ? (
                        <>
                            <Trophy className="h-12 w-12 text-fiji-green mx-auto mb-3" />
                            <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground mb-1">
                                Certified! 🎉
                            </h2>
                            <p className="font-app text-sm text-muted-foreground">
                                Your Certified Bula Agent badge is now active. {result.score_pct}% — {result.correct} / {result.total} correct.
                            </p>
                        </>
                    ) : (
                        <>
                            <RefreshCcw className="h-12 w-12 text-coral mx-auto mb-3" />
                            <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground mb-1">
                                {result.score_pct}% — almost there
                            </h2>
                            <p className="font-app text-sm text-muted-foreground">
                                You need {result.pass_threshold_pct}%+ to certify. Review the lessons below and retake.
                            </p>
                        </>
                    )}
                </div>
                {!result.passed && (result.lessons_to_review || []).length > 0 && (
                    <div className="bg-card border border-border rounded-md p-3 mb-4">
                        <div className="font-app text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Review these lessons</div>
                        <ul className="text-sm font-app text-foreground/85 space-y-1">
                            {result.lessons_to_review.map((lid) => (
                                <li key={lid}>· {lid.replace(/-/g, " ")}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="flex justify-center">
                    <Button onClick={onCancel} className="bg-ocean-deep hover:bg-ocean-deep/90 text-white font-app" data-testid="quiz-back-btn">
                        Back to lessons
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-5 lg:p-7 bg-card" data-testid="quiz-panel">
            <h2 className="font-display text-2xl font-bold text-foreground mb-1">Final certification quiz</h2>
            <p className="font-app text-sm text-muted-foreground mb-6">
                10 questions · ~5 minutes · pass mark {data.pass_threshold_pct}%
            </p>
            <div className="space-y-5">
                {data.questions.map((q, i) => (
                    <div key={q.id} data-testid={`quiz-q-${q.id}`} className="border-b border-border pb-5 last:border-0 last:pb-0">
                        <div className="font-display text-sm font-bold text-foreground mb-3">
                            <span className="text-coral mr-1">Q{i + 1}.</span> {q.question}
                        </div>
                        <div className="space-y-2">
                            {q.options.map((opt, idx) => {
                                const checked = answers[q.id] === idx;
                                return (
                                    <label
                                        key={idx}
                                        className={`flex items-start gap-3 p-2.5 rounded-md cursor-pointer border transition ${checked ? "bg-coral/10 border-coral/40" : "bg-background border-border hover:border-coral/30"}`}
                                    >
                                        <input
                                            type="radio"
                                            name={q.id}
                                            checked={checked}
                                            onChange={() => setAnswer(q.id, idx)}
                                            className="mt-1 accent-coral"
                                            data-testid={`quiz-opt-${q.id}-${idx}`}
                                        />
                                        <span className="font-app text-sm text-foreground/90">{opt}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
                <Button
                    onClick={onSubmit}
                    disabled={submitting}
                    data-testid="quiz-submit-btn"
                    className="bg-coral hover:bg-coral-deep text-white font-app"
                >
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Submit quiz
                </Button>
                <Button variant="ghost" onClick={onCancel} className="font-app" data-testid="quiz-cancel-btn">
                    Cancel
                </Button>
            </div>
        </Card>
    );
}
